import * as React from 'react';
import DocumentTitle from 'react-document-title';
import { createPortal } from 'react-dom';
import { FormattedMessage } from 'react-intl';
// eslint-disable-next-line no-restricted-imports
import styled, { css } from 'styled-components';

import AkAvatar from '@atlaskit/avatar';
import { cssMap } from '@atlaskit/css';
import { Grid as AkGrid, GridColumn as AkGridColumn } from '@atlaskit/page';
import { Box as AkBox, xcss as akXcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { ErrorBoundary } from '@adminhub/error-boundary';
import { PageHeader } from '@adminhub/responsive';
import { useOrgName, useSiteName } from '@adminhub/router';
import { Centered } from '@adminhub/styled';

import { addCollapsedNavProp } from './add-collapsed-nav-prop';
import { InlineEditTitle } from './inline-edit-title';
import { useMainLayoutRightSidebar } from '../hooks/use-main-layout-right-sidebar';
import { formatDocumentTitle } from '../utils/format-document-title';
import { adminMessage } from '../utils/messages';

const styles = cssMap({
  isDisabledColor: {
    color: token('color.text.disabled', '#A5ADBA'),
  },
});

const Subtitle = styled.span`
  font-style: italic;
  font-size: ${token('font.body', '14px')};
  ${({ isDisabled }: { isDisabled: boolean }) =>
    isDisabled
      ? css`
          color: ${token('color.text.disabled', '#A5ADBA')};
        `
      : ''};
`;

const PageBannerWrapper = styled.div`
  margin-top: ${token('space.100', '8px')};
  margin-bottom: ${token('space.negative.050', '-4px')};
`;

const Main = styled.div<{ preventContentOverflow: boolean }>`
  margin-top: ${token('space.negative.100', '-8px')};
  padding-bottom: ${token('space.400', '32px')};
  padding-left: ${token('space.300', '24px')};
  padding-right: ${token('space.300', '24px')};
  contain: ${({ preventContentOverflow }) => (preventContentOverflow ? 'paint' : 'unset')};
`;

const Icon = styled.div`
  line-height: 1;
  margin-right: ${token('space.100', '8px')};
  ${({ isDisabled }: { isDisabled: boolean }) =>
    isDisabled
      ? css`
          filter: grayscale(100%);
        `
      : ''};
`;

const Description = styled.div`
  max-width: 900px;
  margin-top: ${token('space.negative.100', '-8px')};
`;

const Avatar = styled.div`
  padding-right: ${token('space.150', '12px')};
  float: left;
  ${({ isDisabled }: { isDisabled: boolean }) =>
    isDisabled
      ? css`
          filter: grayscale(100%);
        `
      : ''} & > div {
    display: initial !important;
  }
`;

const Children: React.FC<React.PropsWithChildren<{ xcss?: ReturnType<typeof akXcss> }>> = ({ children, xcss }) => {
  const defaultChildrenXcss = akXcss({
    // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
    marginTop: 'space.300',
  });

  return <AkBox xcss={[defaultChildrenXcss, xcss]}>{children}</AkBox>;
};

const AssessmentsLayoutWrapper = styled.div<{ heightCss: string }>`
  height: ${({ heightCss }) => heightCss};
  overflow: auto;
`;

const CustomLayoutHeightWrapper: React.FC<
  React.PropsWithChildren<{
    heightCss?: string;
    children: React.ReactNode;
  }>
> = ({ heightCss, children }) => {
  if (!heightCss) {
    return <>{children}</>;
  }

  return <AssessmentsLayoutWrapper heightCss={heightCss}>{children}</AssessmentsLayoutWrapper>;
};

export type PageType = 'skeleton' | 'error';

export interface MainLayoutProps {
  id: string;
  orgNameForSite?: string;
  pageType?: PageType;
  title?: React.ReactNode;
  inlineEdit?: {
    defaultValue: string;
    errorMessage?: string | null;
    onConfirm(value: string): void;
  };
  isDisabled?: boolean;
  subtitle?: any;
  children?: React.ReactNode;
  description?: any;
  side?: any;
  isFullWidth?: boolean;
  isAutoGridSize?: boolean;
  // Pass a URL to load an avatar
  avatarIconUrl?: string;
  // Pass AkIcon's to be rendered to the left of the title
  icon?: React.ReactNode;
  action?: React.ReactElement;
  breadcrumbs?: React.ReactElement;
  pageParent?: React.ReactNode;
  pageBanner?: React.ReactNode;
  gridLayout?: 'fixed' | 'fluid';
  pageRef?: React.RefObject<HTMLElement>;
  testId?: string;
  childrenContainerStyle?: ReturnType<typeof akXcss>;
  /**
   * This value controls the CSS property "contain: paint". It is useful for hiding overflowing content.
   */
  preventContentOverflow?: boolean;
  /**
   * Use this prop to render a right sidebar from AUK (@atlassian/sidebar).
   * Use the useMainLayoutRightSidebar() hook to control the visibility of this sidebar.
   */
  rightSidebar?: Readonly<React.ReactNode>;
  showTitleOnPage?: boolean;
  /**
   * Use this prop to ensure tables do not take up full width of page.
   */
  excludeTableFullWidth?: boolean;
  mainContentHeightCss?: string;
  disableTitleStyles?: boolean;
  contentAboveDescription?: React.ReactNode;
  errorFallback?: JSX.Element;
  errorCallback?(result: { error: Error }): void;
}

export const MainLayoutImpl: React.FC<MainLayoutProps> = ({
  orgNameForSite,
  title,
  isDisabled = false,
  subtitle,
  action,
  description,
  children,
  side,
  breadcrumbs,
  pageParent,
  pageBanner,
  isAutoGridSize,
  isFullWidth,
  inlineEdit,
  gridLayout,
  pageRef,
  testId = 'main-layout',
  childrenContainerStyle,
  preventContentOverflow = false,
  rightSidebar,
  avatarIconUrl,
  icon,
  showTitleOnPage = true,
  excludeTableFullWidth = false,
  mainContentHeightCss,
  disableTitleStyles = false,
  contentAboveDescription,
  errorFallback,
  errorCallback,
}) => {
  const { hideSidebar } = useMainLayoutRightSidebar();
  React.useEffect(
    () => () => {
      hideSidebar();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const asidePortalEl = document.getElementById('aside-portal');

  const determineChildGridSize = (): number => {
    if (side || !isFullWidth) {
      return 8;
    }

    return 12;
  };

  const renderIcon = (): React.ReactNode => {
    if (avatarIconUrl) {
      return (
        <Avatar isDisabled={isDisabled || false} data-testid="avatar-image">
          <AkAvatar src={avatarIconUrl} size="large" />
        </Avatar>
      );
    }

    if (icon) {
      return <Icon isDisabled={isDisabled || false}>{icon}</Icon>;
    }

    return undefined;
  };

  const onInlineEditConfirm = (value: string) => {
    inlineEdit?.onConfirm(value);
  };

  // Extracts a string from a React node if it's a string
  const extractStringFromElement = (element: React.ReactNode): string => {
    if (typeof element === 'string') {
      return element;
    }

    return '';
  };

  // Extracts strings from an array of React nodes and concatenates them
  const extractStringFromArrayOfElements = (element: React.ReactNode): string => {
    if (Array.isArray(element)) {
      return element
        .map(extractStringFromElementRecursively)
        .filter((message) => message)
        .join(' ');
    }

    return '';
  };

  // Extracts a string from the props of a React element
  const extractStringFromElementProps = (props: {
    children?: React.ReactNode;
    defaultMessage?: string;
    isCustomTitle?: boolean;
    name?: string;
    productName?: string;
    displayName?: string;
  }): string => {
    if (props.isCustomTitle) {
      return props.name || props.productName || props.displayName || '';
    }

    return extractStringFromElementRecursively(props.children);
  };

  // Recursively extracts a string from a React element
  const extractStringFromElementRecursively = (element: React.ReactNode): string => {
    if (React.isValidElement(element)) {
      const props = element.props as {
        children?: React.ReactNode;
        defaultMessage?: string;
        isCustomTitle?: boolean;
        name?: string;
        productName?: string;
        displayName?: string;
      };
      if (
        (typeof element.type === 'function' || typeof element.type === 'object') &&
        'displayName' in element.type &&
        (element.type.displayName === 'FormattedMessage' || element.type.displayName === 'MemoizedFormattedMessage')
      ) {
        return props.defaultMessage || '';
      }

      return extractStringFromElementProps(props);
    }

    return '';
  };

  // Main function to extract a title string from a React node
  const extractTitleString = (element: React.ReactNode): string => {
    if (!element) {
      return '';
    }

    return extractStringFromElement(element) || extractStringFromArrayOfElements(element) || extractStringFromElementRecursively(element);
  };

  const extractedTitle = extractTitleString(title);
  const extractedParentTitle = extractTitleString(pageParent);
  const siteName = useSiteName();
  const orgName = useOrgName() || orgNameForSite;
  const adminTag = <FormattedMessage {...adminMessage} />;
  const admin = adminTag?.props?.defaultMessage && `${adminTag?.props?.defaultMessage}`;
  const docTitle = () => formatDocumentTitle(extractedTitle, extractedParentTitle, siteName, orgName, admin);

  // Updating the page title to be more specific depending on the path of the page.
  return (
    <ErrorBoundary fallback={errorFallback} onError={errorCallback}>
      <div>
        <CustomLayoutHeightWrapper heightCss={mainContentHeightCss}>
          <Main innerRef={pageRef} data-testid={testId} preventContentOverflow={preventContentOverflow}>
            <DocumentTitle title={docTitle()} data-testid="document-title" />
            <AkGrid spacing={!excludeTableFullWidth ? 'cosy' : 'comfortable'} layout={!excludeTableFullWidth ? 'fluid' : gridLayout}>
              {!side && !isFullWidth && <AkGridColumn medium={2} /> /* adds blank space for sidebar */}
              {pageBanner && (
                <AkGridColumn medium={12}>
                  <PageBannerWrapper data-testid="pageBanner">{pageBanner}</PageBannerWrapper>
                </AkGridColumn>
              )}
              <AkGridColumn medium={isAutoGridSize ? undefined : determineChildGridSize()}>
                <PageHeader
                  bottomBar={subtitle && <Subtitle isDisabled={isDisabled}>{subtitle}</Subtitle>}
                  actions={action}
                  breadcrumbs={breadcrumbs}
                  disableTitleStyles={!!inlineEdit || disableTitleStyles}
                >
                  <Centered axis="vertical">
                    {renderIcon()}
                    {showTitleOnPage && title && !inlineEdit && <span css={[isDisabled && styles.isDisabledColor]}>{title}</span>}
                    {inlineEdit && (
                      <InlineEditTitle
                        defaultValue={inlineEdit.defaultValue}
                        errorMessage={inlineEdit.errorMessage}
                        onConfirm={onInlineEditConfirm}
                      />
                    )}
                  </Centered>
                </PageHeader>
                {contentAboveDescription || null}
                {description && <Description>{description}</Description>}
                {children && <Children xcss={childrenContainerStyle}>{children}</Children>}
                {rightSidebar && asidePortalEl && createPortal(rightSidebar, asidePortalEl)}
              </AkGridColumn>
              {side && (
                <AkGridColumn medium={4}>
                  <Children>{side}</Children>
                </AkGridColumn>
              )}
            </AkGrid>
          </Main>
        </CustomLayoutHeightWrapper>
      </div>
    </ErrorBoundary>
  );
};

export const MainLayout = addCollapsedNavProp(
  React.forwardRef((props: MainLayoutProps, ref: React.RefObject<HTMLElement>) => <MainLayoutImpl pageRef={ref} {...props} />),
);
MainLayout.displayName = 'MainLayout';
